/**
 * docSets.ts — WHICH company papers we ask for, keyed on the country the
 * wizard already captured one step earlier (`state.address.country`, ISO-2).
 *
 * ── WHY THIS IS A TABLE AND NOT A FIXED LIST ──────────────────────────────
 * The shipped step asked every company on earth for "Business registration or
 * license · Owner or director ID · Proof of address". A trader in Surat does
 * not have a "business license"; they have a GST registration certificate, a
 * Udyam registration and a company PAN. Asking in a vocabulary the user does
 * not use is how a KYC step becomes a support ticket.
 *
 * ── THREE LOCKED DECISIONS (do not relitigate) ────────────────────────────
 * 1. EXACTLY ONE required tile, in every country. More required tiles = more
 *    abandonment, and the reviewer approves on judgement anyway. Everything
 *    else is labelled "helps" and is genuinely optional.
 * 2. The required tile's `docType` stays `business_license` — NOT
 *    `incorporation`, even though both are in the deployed enum
 *    (`functions/src/me/index.ts` KYC_DOC_TYPES). `scripts/qa/signup-live-e2e.mjs`
 *    and `scripts/qa/signup-wizard-probe.mjs` drive
 *    `[data-testid="signup-doc-business_license"]`, and it is what the admin
 *    review panel already renders. The LABEL says "Certificate of
 *    incorporation"; the enum key is an implementation detail.
 * 3. No registration-NUMBER text fields (DUNS/GST/VAT). `SignupProfileSchema`
 *    is `.strict()` and has no such field, and `Org.registrationNumbers` is
 *    only writable through `PATCH /org`, which is gated behind `ORGS_LIVE`
 *    (off). They would 400 or be silently dropped. The reviewer reads the
 *    numbers off the document.
 *
 * Every `docType` here is already in the DEPLOYED closed enum, so this file
 * needs no backend change to ship.
 *
 * ── SCREEN READERS ────────────────────────────────────────────────────────
 * `spoken` exists because a blind supplier must not hear "DUNS" or "GST" as
 * mangled words. The visible label stays the trade's own abbreviation.
 */

export interface DocTile {
  /** The deployed enum key — never change one that a QA selector drives. */
  docType: string
  /** What the user reads. */
  title: string
  /** One line of plain help underneath. */
  hint: string
  /** Accessible name — abbreviations spelled out. Falls back to `title`. */
  spoken?: string
  /** Exactly one tile per set carries this. */
  required?: boolean
}

/**
 * ONE GENERIC SET (Tej 2026-09-20: "3 documents of proof — not US specific").
 * The country tables (US: seller's permit/W-9/DUNS · IN: GST/PAN) are gone —
 * the trio below is the classic KYB packet every jurisdiction can produce,
 * with local paper names demoted to EXAMPLES in the hints. Legacy docTypes
 * keep labels in TILES_BY_TYPE so old uploads still render.
 */
const GENERIC: DocTile[] = [
  {
    docType: 'business_license',
    title: 'Business registration or incorporation certificate',
    hint: 'Whatever your registrar issues — incorporation, trade licence, GST/Udyam, chamber registration.',
    required: true,
  },
  {
    docType: 'tax',
    title: 'Tax registration certificate',
    hint: 'Your national tax registration — VAT, GST, EIN, PAN, or equivalent.',
    spoken: 'Tax registration certificate',
  },
  {
    docType: 'id',
    title: 'Owner or director ID',
    hint: 'Passport or government photo ID of the principal.',
  },
]

export const DOC_SETS: Record<string, DocTile[]> = { DEFAULT: GENERIC }

/**
 * Revealed by "My papers aren't listed" — the exit for a company whose
 * paperwork this table does not describe. `other` is in the deployed enum.
 */
export const OTHER_TILE: DocTile = {
  docType: 'other',
  title: 'Something else',
  hint: 'Any document that proves the company exists and is yours.',
}

/**
 * The set for a country. Unknown / missing / malformed ⇒ DEFAULT, because the
 * address step can be skipped by a resumed wizard and an empty docket would be
 * a dead end where a generic one is merely less precise.
 */
export function docSetFor(_country?: string | null): DocTile[] {
  // Country no longer selects a set (generic 3-proof packet everywhere); the
  // parameter survives so every wizard call site keeps compiling.
  return DOC_SETS.DEFAULT
}

/**
 * Every tile we can name, by docType — including ones outside the current
 * country's set. A resumed wizard can hold a `gst` document while the address
 * now says US; the packet must still render its real title rather than a raw
 * enum key.
 */
export const TILES_BY_TYPE: Record<string, DocTile> = (() => {
  const map: Record<string, DocTile> = {}
  for (const set of Object.values(DOC_SETS)) for (const t of set) map[t.docType] = map[t.docType] ?? t
  map[OTHER_TILE.docType] = OTHER_TILE
  // Legacy tiles — resumed wizards and old uploads can carry any of these
  // (incl. the retired US/IN country-set types); they must render a real
  // title, never a raw enum key.
  map.id = map.id ?? { docType: 'id', title: 'Owner or director ID', hint: 'Passport or government ID.' }
  map.address_proof = map.address_proof ?? {
    docType: 'address_proof', title: 'Proof of address',
    hint: 'A utility bill or bank letter naming the company.',
  }
  map.sellers_permit = map.sellers_permit ?? { docType: 'sellers_permit', title: "Seller's permit or resale certificate", hint: '' }
  map.w9 = map.w9 ?? { docType: 'w9', title: 'Form W-9', hint: '', spoken: 'Form W-9, W dash nine' }
  map.duns = map.duns ?? { docType: 'duns', title: 'D-U-N-S number certificate', hint: '', spoken: 'D U N S number certificate' }
  map.gst = map.gst ?? { docType: 'gst', title: 'GST registration certificate', hint: '', spoken: 'G S T registration certificate' }
  map.incorporation = map.incorporation ?? { docType: 'incorporation', title: 'Certificate of incorporation', hint: '' }
  map.employment_proof = map.employment_proof ?? { docType: 'employment_proof', title: 'Proof of employment', hint: '' }
  map.passport = map.passport ?? { docType: 'passport', title: 'Passport', hint: '' }
  return map
})()

/** The tile for a docType, with an honest fallback that never renders blank. */
export function tileFor(docType: string): DocTile {
  return TILES_BY_TYPE[docType] ?? { docType, title: docType.replace(/_/g, ' '), hint: '' }
}

/** What a screen reader should say for a tile. */
export function spokenName(tile: DocTile): string {
  return tile.spoken ?? tile.title
}
